import React, { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { cloudFunctions } from "../firebase";
import { AnalyticsService } from "../services/analyticsService";
import { WorkspaceService } from "../services/workspaceService";
import { QRService } from "../services/qrService";
import {
  DailyScanStat,
  LeadSubmission,
  QRCode,
  QRCodeVersion,
  ScanEvent,
} from "../types";

interface Props {
  qrCode: QRCode;
  onBack: () => void;
  onEdit: () => void;
  onChanged: () => Promise<void>;
}

type Tab = "analytics" | "health" | "history" | "leads";
type AnalyticsView = "day" | "week" | "month";

const QRCodeDetails: React.FC<Props> = ({
  qrCode,
  onBack,
  onEdit,
  onChanged,
}) => {
  const [tab, setTab] = useState<Tab>("analytics");
  const [daily, setDaily] = useState<DailyScanStat[]>([]);
  const [events, setEvents] = useState<ScanEvent[]>([]);
  const [versions, setVersions] = useState<QRCodeVersion[]>([]);
  const [leads, setLeads] = useState<LeadSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState("");
  const [analyticsView, setAnalyticsView] = useState<AnalyticsView>("day");
  const [analyticsTimezone, setAnalyticsTimezone] = useState(
    qrCode.schedule?.timezone || "Europe/Berlin",
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [nextDaily, nextEvents, nextVersions, nextLeads] =
          await Promise.all([
            AnalyticsService.getDailyStats(qrCode.id),
            AnalyticsService.getRecentEvents(qrCode.id),
            WorkspaceService.getVersions(qrCode.id),
            WorkspaceService.getLeads(qrCode.id),
          ]);
        setDaily(nextDaily);
        setEvents(nextEvents);
        setVersions(nextVersions);
        setLeads(nextLeads);
      } catch (error) {
        console.error("Detail data failed", error);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [qrCode.id]);

  const analytics = useMemo(() => {
    const today = new Date();
    const days = { day: 30, week: 84, month: 365 }[analyticsView];
    const boundary = new Date(today.getTime() - days * 86400000);
    const previousBoundary = new Date(today.getTime() - days * 2 * 86400000);
    const currentDaily = daily.filter(
      (item) => new Date(`${item.date}T00:00:00`) >= boundary,
    );
    const current = currentDaily.reduce((sum, item) => sum + item.scans, 0);
    const previous = daily
      .filter((item) => {
        const date = new Date(`${item.date}T00:00:00`);
        return date >= previousBoundary && date < boundary;
      })
      .reduce((sum, item) => sum + item.scans, 0);
    const trend = previous
      ? Math.round(((current - previous) / previous) * 100)
      : current
        ? 100
        : 0;
    const currentEvents = events.filter((event) => event.scannedAt >= boundary);
    const trackedTotal = daily.reduce((sum, item) => sum + item.scans, 0);
    const historicalScans = Math.max(0, qrCode.accessCount - trackedTotal);
    const devices = countBy(
      currentEvents,
      (event) => event.device || "Unbekannt",
    );
    const browsers = countBy(
      currentEvents,
      (event) => event.browser || "Unbekannt",
    );
    const operatingSystems = countBy(
      currentEvents,
      (event) => event.os || "Unbekannt",
    );
    const countries = countBy(
      currentEvents,
      (event) => event.country || "Unbekannt",
    );
    const referrers = countBy(currentEvents, (event) =>
      readableReferrer(event.referrer),
    );
    const timeMatrix = buildScanTimeMatrix(currentEvents, analyticsTimezone);
    const hourly = Array.from(
      { length: 24 },
      (_, hour) =>
        [
          `${String(hour).padStart(2, "0")}:00`,
          timeMatrix.reduce((sum, day) => sum + day[hour], 0),
        ] as [string, number],
    ).sort((a, b) => b[1] - a[1]);
    const weekdays = WEEKDAYS.map(
      (day, index) =>
        [day, timeMatrix[index].reduce((sum, count) => sum + count, 0)] as [
          string,
          number,
        ],
    ).sort((a, b) => b[1] - a[1]);
    return {
      current,
      previous,
      trend,
      devices,
      browsers,
      operatingSystems,
      countries,
      referrers,
      newScans: currentEvents.filter((event) => !event.isRepeat).length,
      repeatedScans: currentEvents.filter((event) => event.isRepeat).length,
      trackedTotal,
      historicalScans,
      currentEvents,
      timeMatrix,
      topHour: hourly[0],
      topWeekday: weekdays[0],
      chartData: aggregateStats(currentDaily, analyticsView),
    };
  }, [daily, events, analyticsView, analyticsTimezone, qrCode.accessCount]);

  const checkHealth = async () => {
    setChecking(true);
    setMessage("");
    try {
      await httpsCallable(
        cloudFunctions,
        "checkQRHealthNow",
      )({ qrId: qrCode.id });
      await onChanged();
      setMessage("Health-Check abgeschlossen.");
    } catch (error) {
      setMessage("Health-Check konnte nicht ausgeführt werden.");
      console.error(error);
    } finally {
      setChecking(false);
    }
  };

  const restoreVersion = async (version: QRCodeVersion) => {
    if (!version.snapshot?.target) return;
    await QRService.updateQRCode(qrCode.id, {
      ...version.snapshot,
      id: qrCode.id,
      title: qrCode.title || "",
      description: qrCode.description || "",
      changeComment: `Wiederhergestellt auf Version vom ${version.changedAt.toLocaleString("de-DE")}`,
    });
    await onChanged();
    setMessage("Vorherige Version wurde wiederhergestellt.");
  };

  const chartData = analytics.chartData;
  const chartMax = Math.max(1, ...chartData.map((item) => item.scans));

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <button
            type="button"
            onClick={onBack}
            className="text-sm font-semibold text-blue-600"
          >
            ← Zur Bibliothek
          </button>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight">
            {qrCode.title || qrCode.id}
          </h2>
          <p className="mt-1 break-all text-sm text-slate-500">
            {qrCode.target}
          </p>
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="app-primary-button justify-center"
        >
          QR-Code bearbeiten
        </button>
      </div>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Metric
          label="Gesamtscans"
          value={qrCode.accessCount.toLocaleString("de-DE")}
          detail={
            analytics.historicalScans
              ? `${analytics.historicalScans.toLocaleString("de-DE")} historisch`
              : undefined
          }
        />
        <Metric
          label={
            {
              day: "Letzte 30 Tage",
              week: "Letzte 12 Wochen",
              month: "Letzte 12 Monate",
            }[analyticsView]
          }
          value={analytics.current.toLocaleString("de-DE")}
          detail={`${analytics.trend >= 0 ? "+" : ""}${analytics.trend}%`}
        />
        <Metric
          label="Neue Scans"
          value={analytics.newScans.toLocaleString("de-DE")}
        />
        <Metric
          label="Wiederholte Scans"
          value={analytics.repeatedScans.toLocaleString("de-DE")}
        />
        <Metric
          label="Health"
          value={healthLabel(qrCode.health?.status)}
          detail={
            qrCode.health?.responseTimeMs
              ? `${qrCode.health.responseTimeMs} ms`
              : undefined
          }
        />
      </section>

      <div className="flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1">
        {(
          [
            ["analytics", "Analytics"],
            ["health", "Health Monitor"],
            ["history", "Versionen"],
            ["leads", `Leads (${leads.length})`],
          ] as Array<[Tab, string]>
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold ${tab === key ? "bg-slate-950 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {message && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
          {message}
        </div>
      )}
      {loading ? (
        <div className="app-panel animate-pulse p-12 text-center text-sm text-slate-400">
          Daten werden geladen …
        </div>
      ) : (
        <>
          {tab === "analytics" && (
            <div className="space-y-6">
              <div className="grid gap-6 xl:grid-cols-[minmax(0,1.5fr)_minmax(300px,.7fr)]">
                <section className="app-panel p-5 sm:p-6">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="font-semibold">Scan-Verlauf</h3>
                      <p className="mt-1 text-sm text-slate-500">
                        Tages-, Wochen- oder Monatswerte mit Vergleichszeitraum.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(
                        [
                          ["day", "Tag"],
                          ["week", "Woche"],
                          ["month", "Monat"],
                        ] as Array<[AnalyticsView, string]>
                      ).map(([view, label]) => (
                        <button
                          key={view}
                          type="button"
                          onClick={() => setAnalyticsView(view)}
                          className={
                            analyticsView === view
                              ? "app-primary-button"
                              : "app-secondary-button"
                          }
                        >
                          {label}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          AnalyticsService.exportEventsCSV(
                            events,
                            `${qrCode.id}-scans.csv`,
                            qrCode,
                            daily,
                          )
                        }
                        className="app-secondary-button"
                      >
                        CSV
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          void AnalyticsService.exportPDFReport(
                            qrCode,
                            daily,
                            events,
                          )
                        }
                        className="app-secondary-button"
                      >
                        PDF-Bericht
                      </button>
                    </div>
                  </div>
                  {analytics.historicalScans > 0 && (
                    <div className="mt-6 flex flex-col gap-4 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-blue-950 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold">
                          {analytics.historicalScans.toLocaleString("de-DE")}{" "}
                          historische Scans sind erhalten
                        </p>
                        <p className="mt-1 max-w-2xl text-xs leading-5 text-blue-800">
                          Diese Scans wurden gezählt, bevor detaillierte
                          Analytics aktiv waren. Zeitpunkt, Gerät und Browser
                          lassen sich rückwirkend nicht zuverlässig
                          rekonstruieren.
                        </p>
                      </div>
                      <div className="flex flex-shrink-0 gap-2 text-center">
                        <div className="rounded-xl bg-white px-4 py-3 shadow-sm">
                          <p className="text-xl font-semibold">
                            {analytics.trackedTotal.toLocaleString("de-DE")}
                          </p>
                          <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                            Detailliert
                          </p>
                        </div>
                        <div className="rounded-xl bg-blue-600 px-4 py-3 text-white shadow-sm">
                          <p className="text-xl font-semibold">
                            {analytics.historicalScans.toLocaleString("de-DE")}
                          </p>
                          <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-blue-100">
                            Historisch
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mt-8 flex h-56 items-end gap-1.5 border-b border-slate-200">
                    {chartData.length ? (
                      chartData.map((item) => (
                        <div
                          key={item.date}
                          className="group relative flex h-full min-w-0 flex-1 items-end"
                        >
                          <div
                            className="w-full rounded-t bg-blue-500 transition hover:bg-blue-600"
                            style={{
                              height: `${Math.max(3, (item.scans / chartMax) * 100)}%`,
                            }}
                            title={`${item.date}: ${item.scans} Scans`}
                          />
                        </div>
                      ))
                    ) : (
                      <div className="m-auto max-w-sm text-center">
                        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-blue-50 text-xl font-semibold text-blue-700">
                          {analytics.historicalScans.toLocaleString("de-DE")}
                        </div>
                        <p className="mt-3 text-sm font-semibold text-slate-700">
                          Historischer Gesamtwert vorhanden
                        </p>
                        <p className="mt-1 text-xs leading-5 text-slate-400">
                          Der Zeitverlauf füllt sich automatisch ab dem nächsten
                          Scan.
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 flex justify-between text-xs text-slate-400">
                    <span>{chartData[0]?.date || "–"}</span>
                    <span>
                      {chartData[chartData.length - 1]?.date || "Heute"}
                    </span>
                  </div>
                </section>
                <aside className="space-y-6">
                  <Breakdown
                    title="Geräte"
                    values={analytics.devices}
                    hasHistoricalData={analytics.historicalScans > 0}
                  />
                  <Breakdown
                    title="Browser"
                    values={analytics.browsers}
                    hasHistoricalData={analytics.historicalScans > 0}
                  />
                  <Breakdown
                    title="Betriebssysteme"
                    values={analytics.operatingSystems}
                    hasHistoricalData={analytics.historicalScans > 0}
                  />
                  <Breakdown
                    title="Länder"
                    values={analytics.countries}
                    hasHistoricalData={analytics.historicalScans > 0}
                  />
                  <Breakdown
                    title="Referrer"
                    values={analytics.referrers}
                    hasHistoricalData={analytics.historicalScans > 0}
                  />
                </aside>
              </div>
              <ScanTimeAnalysis
                events={analytics.currentEvents}
                timeMatrix={analytics.timeMatrix}
                timezone={analyticsTimezone}
                onTimezoneChange={setAnalyticsTimezone}
                topHour={analytics.topHour}
                topWeekday={analytics.topWeekday}
                historicalScans={analytics.historicalScans}
              />
            </div>
          )}
          {tab === "health" && (
            <section className="app-panel p-5 sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="font-semibold">Ziel-URL Health</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    HTTP-Status, Antwortzeit, HTTPS und Redirect-Kette.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void checkHealth()}
                  disabled={checking}
                  className="app-primary-button justify-center"
                >
                  {checking ? "Prüfung läuft …" : "Jetzt prüfen"}
                </button>
              </div>
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                <Metric
                  label="Status"
                  value={healthLabel(qrCode.health?.status)}
                />
                <Metric
                  label="HTTP"
                  value={String(qrCode.health?.statusCode || "–")}
                />
                <Metric
                  label="Antwortzeit"
                  value={
                    qrCode.health?.responseTimeMs
                      ? `${qrCode.health.responseTimeMs} ms`
                      : "–"
                  }
                />
                <Metric
                  label="HTTPS"
                  value={
                    qrCode.health?.sslValid === undefined
                      ? "–"
                      : qrCode.health.sslValid
                        ? "Aktiv"
                        : "Nicht aktiv"
                  }
                />
                <Metric
                  label="SSL gültig bis"
                  value={
                    qrCode.health?.sslExpiresAt
                      ? new Date(qrCode.health.sslExpiresAt).toLocaleDateString(
                          "de-DE",
                        )
                      : "–"
                  }
                  detail={
                    qrCode.health?.sslDaysRemaining !== undefined
                      ? `${qrCode.health.sslDaysRemaining} Tage`
                      : undefined
                  }
                />
              </div>
              <div className="mt-6 rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
                {qrCode.health?.message ||
                  "Der erste automatische Health-Check steht noch aus."}
              </div>
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                <strong>Sicheres Fallback:</strong>{" "}
                {qrCode.fallbackTarget || qrCode.target}
              </div>
            </section>
          )}
          {tab === "history" && (
            <section className="app-panel overflow-hidden">
              <div className="border-b border-slate-200 p-5">
                <h3 className="font-semibold">Änderungsverlauf</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Bis zu 50 sichere Konfigurationsstände.
                </p>
              </div>
              {versions.length ? (
                <div className="divide-y divide-slate-100">
                  {versions.map((version) => (
                    <div
                      key={version.id}
                      className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold">
                          {version.comment || "Konfiguration geändert"}
                        </p>
                        <p className="mt-1 truncate text-xs text-slate-500">
                          {version.previousTarget} → {version.newTarget}
                        </p>
                        <p className="mt-2 text-xs text-slate-400">
                          {version.changedAt.toLocaleString("de-DE")}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => void restoreVersion(version)}
                        className="app-secondary-button justify-center"
                      >
                        Diese Version wiederherstellen
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty text="Versionen werden ab der nächsten Änderung automatisch gespeichert." />
              )}
            </section>
          )}
          {tab === "leads" && (
            <section className="app-panel overflow-hidden">
              <div className="border-b border-slate-200 p-5">
                <h3 className="font-semibold">Kontaktanfragen</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Über die mobile Landingpage eingegangene Leads.
                </p>
              </div>
              {leads.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-400">
                      <tr>
                        <th className="p-4">Zeitpunkt</th>
                        <th className="p-4">Name</th>
                        <th className="p-4">E-Mail</th>
                        <th className="p-4">Telefon</th>
                        <th className="p-4">Nachricht</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {leads.map((lead) => (
                        <tr key={lead.id}>
                          <td className="whitespace-nowrap p-4">
                            {lead.createdAt.toLocaleString("de-DE")}
                          </td>
                          <td className="p-4">{lead.name || "–"}</td>
                          <td className="p-4">{lead.email || "–"}</td>
                          <td className="p-4">{lead.phone || "–"}</td>
                          <td className="max-w-sm p-4">
                            {lead.message || "–"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Empty text="Noch keine Kontaktanfragen vorhanden." />
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
};

const Metric: React.FC<{ label: string; value: string; detail?: string }> = ({
  label,
  value,
  detail,
}) => (
  <div className="app-panel p-5">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
      {label}
    </p>
    <div className="mt-2 flex items-end gap-2">
      <p className="text-2xl font-semibold tracking-tight">{value}</p>
      {detail && (
        <span className="pb-0.5 text-xs font-semibold text-blue-600">
          {detail}
        </span>
      )}
    </div>
  </div>
);
const Breakdown: React.FC<{
  title: string;
  values: Array<[string, number]>;
  hasHistoricalData?: boolean;
}> = ({ title, values, hasHistoricalData = false }) => (
  <section className="app-panel p-5">
    <h3 className="text-sm font-semibold">{title}</h3>
    <div className="mt-4 space-y-3">
      {values.length ? (
        values.slice(0, 6).map(([label, count]) => (
          <div key={label}>
            <div className="flex justify-between text-xs">
              <span>{label}</span>
              <strong>{count}</strong>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-blue-500"
                style={{
                  width: `${Math.max(5, (count / Math.max(1, values[0][1])) * 100)}%`,
                }}
              />
            </div>
          </div>
        ))
      ) : (
        <div>
          <p className="text-sm text-slate-400">Noch keine Detaildaten.</p>
          {hasHistoricalData && (
            <p className="mt-2 text-xs leading-5 text-slate-400">
              Historische Scans enthalten keine rückwirkend rekonstruierbaren
              Angaben.
            </p>
          )}
        </div>
      )}
    </div>
  </section>
);
const ScanTimeAnalysis: React.FC<{
  events: ScanEvent[];
  timeMatrix: number[][];
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
  topHour: [string, number];
  topWeekday: [string, number];
  historicalScans: number;
}> = ({
  events,
  timeMatrix,
  timezone,
  onTimezoneChange,
  topHour,
  topWeekday,
  historicalScans,
}) => {
  const maximum = Math.max(1, ...timeMatrix.flat());
  const recent = [...events]
    .sort((a, b) => b.scannedAt.getTime() - a.scannedAt.getTime())
    .slice(0, 20);
  const formatter = new Intl.DateTimeFormat("de-DE", {
    timeZone: timezone,
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return (
    <section className="app-panel overflow-hidden">
      <div className="flex flex-col gap-4 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <div>
          <h3 className="font-semibold">Tage und Uhrzeiten</h3>
          <p className="mt-1 text-sm text-slate-500">
            Wann dieser QR-Code am häufigsten gescannt wird.
          </p>
        </div>
        <label className="text-xs font-semibold text-slate-500">
          Zeitzone
          <select
            value={timezone}
            onChange={(event) => onTimezoneChange(event.target.value)}
            className="ml-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
          >
            <option value="Europe/Berlin">Berlin</option>
            <option value="Europe/London">London</option>
            <option value="America/New_York">New York</option>
            <option value="America/Los_Angeles">Los Angeles</option>
            <option value="Asia/Tokyo">Tokio</option>
            <option value="UTC">UTC</option>
          </select>
        </label>
      </div>

      <div className="grid gap-3 border-b border-slate-200 bg-slate-50/70 p-5 sm:grid-cols-3 sm:p-6">
        <TimeMetric
          label="Stärkster Wochentag"
          value={topWeekday[1] ? topWeekday[0] : "Noch offen"}
          detail={topWeekday[1] ? `${topWeekday[1]} Scans` : undefined}
        />
        <TimeMetric
          label="Stärkste Uhrzeit"
          value={topHour[1] ? topHour[0] : "Noch offen"}
          detail={topHour[1] ? `${topHour[1]} Scans` : undefined}
        />
        <TimeMetric
          label="Zeitlich erfasst"
          value={events.length.toLocaleString("de-DE")}
          detail={`${timezone.replace("_", " ")}`}
        />
      </div>

      {events.length ? (
        <>
          <div className="overflow-x-auto p-5 sm:p-6">
            <div className="min-w-[760px]">
              <div className="mb-2 grid grid-cols-[52px_repeat(24,minmax(24px,1fr))] gap-1">
                <span />
                {Array.from({ length: 24 }, (_, hour) => (
                  <span
                    key={hour}
                    className="text-center text-[9px] font-medium text-slate-400"
                  >
                    {hour % 3 === 0 ? String(hour).padStart(2, "0") : ""}
                  </span>
                ))}
              </div>
              <div className="space-y-1.5">
                {WEEKDAYS.map((day, dayIndex) => (
                  <div
                    key={day}
                    className="grid grid-cols-[52px_repeat(24,minmax(24px,1fr))] gap-1"
                  >
                    <span className="self-center text-xs font-semibold text-slate-500">
                      {day.slice(0, 2)}
                    </span>
                    {timeMatrix[dayIndex].map((count, hour) => (
                      <div
                        key={hour}
                        className="h-7 rounded-[5px] border border-blue-100/60"
                        style={{
                          backgroundColor: count
                            ? `rgba(37, 99, 235, ${0.16 + (count / maximum) * 0.78})`
                            : "rgba(241, 245, 249, .7)",
                        }}
                        title={`${day}, ${String(hour).padStart(2, "0")}:00–${String((hour + 1) % 24).padStart(2, "0")}:00: ${count} Scans`}
                      />
                    ))}
                  </div>
                ))}
              </div>
              <div className="mt-3 flex justify-end gap-2 text-[10px] text-slate-400">
                <span>Weniger</span>
                {[0.12, 0.32, 0.55, 0.78].map((opacity) => (
                  <span
                    key={opacity}
                    className="h-3 w-5 rounded-sm bg-blue-600"
                    style={{ opacity }}
                  />
                ))}
                <span>Mehr</span>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200">
            <div className="px-5 py-4 sm:px-6">
              <h4 className="text-sm font-semibold">
                Letzte Scans mit Uhrzeit
              </h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="px-5 py-3 sm:px-6">Zeitpunkt</th>
                    <th className="px-5 py-3">Besucher</th>
                    <th className="px-5 py-3">Gerät</th>
                    <th className="px-5 py-3">Browser</th>
                    <th className="px-5 py-3">Land</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recent.map((event) => (
                    <tr key={event.id} className="hover:bg-slate-50/70">
                      <td className="whitespace-nowrap px-5 py-3 font-medium text-slate-800 sm:px-6">
                        {formatter.format(event.scannedAt)}
                      </td>
                      <td className="px-5 py-3 text-slate-500">
                        {event.isRepeat ? "Wiederholt" : "Neu"}
                      </td>
                      <td className="px-5 py-3 text-slate-500">
                        {event.device || "Unbekannt"}
                      </td>
                      <td className="px-5 py-3 text-slate-500">
                        {event.browser || "Unbekannt"}
                      </td>
                      <td className="px-5 py-3 text-slate-500">
                        {event.country || "Unbekannt"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="p-8 text-center sm:p-12">
          <p className="text-sm font-semibold text-slate-700">
            Noch keine Scans mit Zeitstempel
          </p>
          <p className="mx-auto mt-2 max-w-xl text-xs leading-5 text-slate-400">
            Wochentage und Uhrzeiten erscheinen ab dem nächsten detailliert
            erfassten Scan in der gewählten Zeitzone.
            {historicalScans > 0 &&
              ` Die ${historicalScans} historischen Scans besitzen keinen gespeicherten Zeitpunkt.`}
          </p>
        </div>
      )}
    </section>
  );
};
const TimeMetric: React.FC<{
  label: string;
  value: string;
  detail?: string;
}> = ({ label, value, detail }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
      {label}
    </p>
    <div className="mt-2 flex items-end gap-2">
      <strong className="text-lg text-slate-900">{value}</strong>
      {detail && (
        <span className="pb-0.5 text-xs text-slate-500">{detail}</span>
      )}
    </div>
  </div>
);
const Empty: React.FC<{ text: string }> = ({ text }) => (
  <div className="p-12 text-center text-sm text-slate-400">{text}</div>
);
const countBy = <T,>(
  items: T[],
  selector: (item: T) => string,
): Array<[string, number]> =>
  Object.entries(
    items.reduce<Record<string, number>>((result, item) => {
      const key = selector(item);
      result[key] = (result[key] || 0) + 1;
      return result;
    }, {}),
  ).sort((a, b) => b[1] - a[1]);
const readableReferrer = (value?: string): string => {
  if (!value) return "Direkt / unbekannt";
  try {
    return new URL(value).hostname || "Direkt / unbekannt";
  } catch {
    return value.slice(0, 60);
  }
};
const WEEKDAYS = [
  "Montag",
  "Dienstag",
  "Mittwoch",
  "Donnerstag",
  "Freitag",
  "Samstag",
  "Sonntag",
];
export const zonedScanParts = (date: Date, timeZone: string) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    hour: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const weekday = parts.find((part) => part.type === "weekday")?.value || "Mon";
  const hour = Number(parts.find((part) => part.type === "hour")?.value || 0);
  const weekdayIndex = [
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
    "Sun",
  ].indexOf(weekday);
  return { weekdayIndex: Math.max(0, weekdayIndex), hour };
};
export const buildScanTimeMatrix = (
  events: Pick<ScanEvent, "scannedAt">[],
  timeZone: string,
) => {
  const matrix = Array.from({ length: 7 }, () =>
    Array.from({ length: 24 }, () => 0),
  );
  events.forEach((event) => {
    const { weekdayIndex, hour } = zonedScanParts(event.scannedAt, timeZone);
    matrix[weekdayIndex][hour] += 1;
  });
  return matrix;
};
const aggregateStats = (
  items: DailyScanStat[],
  view: AnalyticsView,
): DailyScanStat[] => {
  const grouped = new Map<string, DailyScanStat>();
  items.forEach((item) => {
    const date = new Date(`${item.date}T00:00:00`);
    let key = item.date;
    if (view === "week") {
      const day = date.getDay() || 7;
      date.setDate(date.getDate() - day + 1);
      key = date.toISOString().slice(0, 10);
    } else if (view === "month") key = item.date.slice(0, 7);
    const current = grouped.get(key) || {
      date: key,
      scans: 0,
      uniqueVisitors: 0,
    };
    current.scans += item.scans;
    current.uniqueVisitors += item.uniqueVisitors;
    grouped.set(key, current);
  });
  return Array.from(grouped.values()).sort((a, b) =>
    a.date.localeCompare(b.date),
  );
};
const healthLabel = (
  status?: QRCode["health"] extends infer H
    ? H extends { status: infer S }
      ? S
      : never
    : never,
) =>
  ({
    active: "Aktiv",
    unscanned: "Ungescannt",
    declining: "Sinkend",
    unreachable: "Nicht erreichbar",
    slow: "Langsam",
    inactive: "Inaktiv",
    checking: "Wird geprüft",
  })[String(status)] || "Noch nicht geprüft";

export default QRCodeDetails;
